import React, { createContext, useState, useEffect, useCallback, type ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  username?: string;
  fullName?: string;
  city?: string;
  state?: string;
  country?: string;
  profileIncomplete?: boolean;
  needsProfileUpdate?: boolean;
  userTypes?: unknown[];
  userType?: string | null;
  role?: 'creator' | 'fan' | 'venue' | null;
  genres?: string[];
  tags?: string[];
  profPics?: string;
  followerCount?: number;
  followingCount?: number;
  isFollowing?: boolean;
  views?: number;
  dateOfBirth?: string;
  website?: string | null;
  instagram?: string | null;
  twitter?: string | null;
  tiktok?: string | null;
  coverPhoto?: string | null;
  bio?: string | null;
  gender?: string | null;
  phoneNumber?: string | null;
  spotify?: string | null;
  appleMusic?: string | null;
  soundcloud?: string | null;
  amazonMusic?: string | null;
  youtubeMusic?: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, userData: User, refreshToken?: string) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  refetch: () => void;
}

import { useUserProfileQuery } from '../api/auth';
import { useQueryClient } from '@tanstack/react-query';
import { signupStore } from '../utils/signupStore';
import { useUserStore } from '../store/useUserStore';
import { locationHistoryService } from '../services/locationHistoryService';
import { onTokenRefreshedCallbacks } from '../api/api';

export const mapUserFromAuthResponse = (rawUser: any): User => {
  const userTypes = rawUser.user_types ?? [];
  return {
    id: String(rawUser.id ?? ''),
    email: rawUser.email ?? '',
    username: rawUser.username,
    fullName: rawUser.full_name ?? rawUser.fullName,
    city: rawUser.city,
    state: rawUser.state,
    country: rawUser.country,
    genres: rawUser.genres ?? [],
    tags: rawUser.tags ?? rawUser.user_tags ?? [],
    userTypes,
    profileIncomplete: rawUser.profile_incomplete ?? false,
    needsProfileUpdate: rawUser.needs_profile_update ?? false,
    userType: rawUser.user_type ?? rawUser.userType ?? null,
    role: rawUser.role ?? (userTypes.length > 0 ? userTypes[0] : null),
    profPics:
      rawUser.prof_pics ?? rawUser.prof_pics_url ?? rawUser.google_prof_pics ?? rawUser.profPics,
    followerCount: rawUser.follower_count,
    followingCount: rawUser.following_count,
    isFollowing: rawUser.is_following,
    views: rawUser.views,
    dateOfBirth: rawUser.date_of_birth,
    website: rawUser.website,
    instagram: rawUser.instagram,
    twitter: rawUser.twitter,
    tiktok: rawUser.tiktok,
    coverPhoto: rawUser.cover_photo ?? rawUser.cover_photo_url ?? rawUser.coverPhoto,
    bio: rawUser.bio,
    gender: rawUser.gender,
    phoneNumber: rawUser.phone_number ?? rawUser.phoneNumber,
    spotify: rawUser.spotify,
    appleMusic: rawUser.apple_music ?? rawUser.appleMusic,
    soundcloud: rawUser.soundcloud,
    amazonMusic: rawUser.amazon_music ?? rawUser.amazonMusic,
    youtubeMusic: rawUser.youtube_music ?? rawUser.youtubeMusic,
  };
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  // Sync hydration from localStorage happens immediately in useState to prevent redirect races
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('user_data');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('access_token'));

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('user_id');

    // Clear signup draft
    signupStore.clear();

    // Clear location search history to prevent cross-account leakage
    locationHistoryService.clearHistory();

    // Clear Zustand store if it exists
    useUserStore.getState().logout();

    // Clear React Query cache
    queryClient.clear();

    setToken(null);
    setUser(null);
  }, []);

  const login = useCallback((newToken: string, userData: User, refreshToken?: string) => {
    localStorage.setItem('access_token', newToken);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    localStorage.setItem('user_data', JSON.stringify(userData));
    localStorage.setItem('user_id', userData.id);
    setToken(newToken);
    setUser(userData);

    // Sync Zustand
    useUserStore.getState().setUser({ user: userData });
  }, []);

  const updateUser = useCallback((userData: Partial<User>) => {
    setUser((prev) => {
      const current = prev || JSON.parse(localStorage.getItem('user_data') || 'null');
      if (!current && !userData.id) return null;

      const updated = { ...(current || {}), ...userData } as User;
      localStorage.setItem('user_data', JSON.stringify(updated));
      if (updated.id) {
        localStorage.setItem('user_id', updated.id);
      }

      // Sync Zustand
      useUserStore.getState().setUser({ user: updated });

      console.log('DEBUG: User state updated:', updated);
      return updated;
    });
  }, []);

  // Use Query for automatic hydration - ensure it uses the most reliable ID source
  const currentUserId = user?.id || localStorage.getItem('user_id');
  const { data: profileData, refetch } = useUserProfileQuery(currentUserId);

  useEffect(() => {
    if (profileData) {
      console.log('DEBUG: AuthContext sync - Profile Data:', profileData);
      const mappedData = mapUserFromAuthResponse(profileData);
      updateUser(mappedData);
    }
  }, [profileData, updateUser]);

  useEffect(() => {
    const handleTokenRefreshed = (access: string) => {
      setToken(access);
    };
    onTokenRefreshedCallbacks.push(handleTokenRefreshed);
    return () => {
      const idx = onTokenRefreshedCallbacks.indexOf(handleTokenRefreshed);
      if (idx !== -1) onTokenRefreshedCallbacks.splice(idx, 1);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading: false,
        login,
        logout,
        updateUser,
        refetch: () => refetch(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
